import React, {useState} from "react";
import {
    Button,
    Checkbox,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {api} from "../../../../api/API";
import {AutoComplete} from "../../shopdrawing/openings/AutoComplete";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {Opening, ScreenElevation} from "../../../../api/Openings";
import {useProjectId, useProjectUnitSystem} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {useIsQuote} from "../QuoteContainer";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useFilePicker} from "../../shopdrawing/extras/FilePicker";
import {useAsyncAction} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import {imperialParser, metricParser} from "../../../../misc/scroller/Table";
import {getAll} from "../../../logistics/routes/useGetAll";
import {ext} from "../../../../api/Files";

export type ScreenElevationCompat = {
    screenElevation: string;
    screenElevationRenameOnlyFlag?: boolean;
    screenElevationFile?: number;
    screenElevationFileName?: string;
    screenElevationOutsideHeight?: string;
    screenElevationOutsideWidth?: string;
    hasFixedMullion: boolean;
}

export function ScreenElevationPopup<T extends (Opening|QuoteOpening|ScreenElevationCompat)>(props: {
    row: T;
    onCancel(): void;
    onDone?(value: T): Promise<any>;
    requireNew?: boolean;
}) {
    const filePicker = useFilePicker({
        accept: ["application/pdf", "image/*"]
    });

    const [name, setName] = useState(props.row.screenElevation);
    const [height, setHeight] = useState(props.row.screenElevationOutsideHeight || "")
    const [width, setWidth] = useState(props.row.screenElevationOutsideWidth || "")
    const [fixedMullion, setFixedMullion] = useState(props.row.hasFixedMullion || false);
    const [file, setFile] = useState(props.row.screenElevationFile ? {name: props.row.screenElevationFileName, id: props.row.screenElevationFile} : null);
    const [isRename, setIsRename] = useState(false);
    const projectId = useProjectId();
    const alt = useAlternative();
    const isQuote = useIsQuote();

    const [isCreate, setIsCreate] = useState(false);
    const preventEditForRequireNew = props.requireNew && !isCreate;

    const onDoneRef = useSyncedRef(props.onDone);
    const doneCb = useAsyncAction(async (input) => {
        if(!onDoneRef.current) return;
        await onDoneRef.current(input)
    }, []);

    const upload = useAsyncAction(async (file: File) => {
        const fileObj = await api.files.upload({
            // use guid naming to prevent collisions
            name: window.crypto.randomUUID() + ext(file.name),
            convert2pdf: true,
            file: file,
        });

        setFile({
            id: fileObj.id,
            name: file.name,
        });
    }, [filePicker]);

    const size = useWindowSize();
    const sys = useProjectUnitSystem()
    if(!sys) return null

    const parser = sys === "metric" ? metricParser : imperialParser;
    const disabled = !props.onDone;

    const submit = () => {
        if(!props.onDone) return;
        doneCb.callback(Object.assign({}, props.row, {
            screenElevation: name,
            screenElevationFile: file ? file.id : -1,
            screenElevationOutsideHeight: parser(height, height),
            screenElevationOutsideWidth: parser(width, width),
            hasFixedMullion: fixedMullion,
            screenElevationRenameOnlyFlag: isQuote ? isRename : undefined,
        }))
    };

    return (
        <Dialog2 open onClose={props.onCancel} fullWidth maxWidth="lg" onEnterPress={submit}>
            <DialogTitle>Screen Elevation</DialogTitle>
            <DialogContent>
                <div style={{height: 8}} />
                <Grid container spacing={1}>
                    <Grid item xs={8}>
                        {file ? <iframe
                            src={api.files.downloadLink(file.id, "inline")}
                            style={{
                                height: size.height * 0.75,
                                width: "100%",
                                border: "1px solid black",
                            }}
                        ></iframe> : <div>Choose a file on the right</div>}
                    </Grid>
                    <Grid item xs={4}>
                        <AutoComplete<ScreenElevation>
                            fullWidth
                            label="Name"
                            size="small"
                            disabled={disabled}
                            search={async input => {

                                let result = await getAll(null, offset =>
                                    isQuote ? api.quoteOpenings.listScreenElevations({
                                            project: projectId,
                                            alternative: alt,
                                            offset: offset,
                                        }) :
                                        api.openings.listScreenElevations({
                                            project: projectId,
                                            offset: offset,
                                        }))

                                if (input !== "") {
                                    const value = input;
                                    input = input.toLowerCase();
                                    result = result.filter(r => r.name.toLowerCase().indexOf(input) !== -1)

                                    if(file && isQuote && !props.requireNew) {
                                        if (!result.find(v => v.name.toLowerCase() === input.toLowerCase())) {
                                            result.push({
                                                id: Date.now(),
                                                name: "Update to '" + value + "' (no price change)",
                                                value: value,
                                                isUpdateName: true,
                                            } as any)
                                        }
                                    }
                                }

                                return result;
                            }}
                            value={{name: name, fileId: file}}
                            equals={(a, b) => a.name === b.name}
                            makeLabel={a => a.name}
                            onSelect={value => {
                                setIsCreate(false);

                                if("isUpdateName" in value && value.isUpdateName) {
                                    // @ts-ignore
                                    setName(value.value);
                                    setIsRename(true)
                                    return;
                                }

                                setIsRename(false)

                                console.log("select", value);
                                setFile(value.file ? {
                                    id: value.file,
                                    name: value.fileName || "",
                                } : null);

                                setWidth(value.width || "")
                                setHeight(value.height || "")
                                setName(value.name);
                            }}
                            onCreate={async name => {
                                setIsCreate(true);
                                console.log("create", name)
                                setName(name);
                                setFile(null);
                            }}
                        />

                        <div style={{height: 24}}/>

                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs>
                                {file ? file.name : "No file selected"}
                            </Grid>
                            <Grid item>
                                <Button
                                    disabled={disabled || preventEditForRequireNew}
                                    variant="outlined" size="small" onClick={async () => {

                                    const files = await filePicker();
                                    upload.callback(files[0])
                                }}>
                                    Choose File
                                </Button>
                            </Grid>
                            {file && <Grid item>
                                <Button variant="outlined" size="small" disabled={disabled || preventEditForRequireNew} onClick={() => setFile(null)}>
                                    Clear File
                                </Button>
                            </Grid>}
                        </Grid>

                        <div style={{height: 24}}/>

                        <TextField label="Outside Opening Width" value={width} disabled={disabled || preventEditForRequireNew}
                                   onChange={e => {
                                        setWidth(e.target.value)
                                    }}
                                   onBlur={e => {
                                       setWidth(parser(width, width))
                                   }}
                        />
                        <div style={{height: 8}} />
                        <TextField label="Outside Opening Height"
                                   value={height} disabled={disabled || preventEditForRequireNew}
                                    onChange={e => {
                                         setHeight(e.target.value)
                                    }}
                                   onBlur={() => {
                                         setHeight(parser(height, height))
                                   }}
                        />
                        <div style={{height: 8}} />
                        <FormControlLabel control={<Checkbox
                            checked={fixedMullion}
                            disabled={disabled || preventEditForRequireNew}
                        />} label="Has Fixed Mullion" disabled={disabled || preventEditForRequireNew} onClick={() => {
                            setFixedMullion(!fixedMullion)
                        }} />
                    </Grid>
                </Grid>

            </DialogContent>
            <SmoothActionBar
                loading={doneCb.loading}
                error={doneCb.error}
                actionName="Done"
                onCancel={props.onCancel}
                onSubmit={props.onDone ? submit : undefined}
            />
        </Dialog2>
    )
}